<template>
  <div class="team-page">
    <!-- 首页同款蓝黑金色背景 -->
    <div class="network-grid-layer"></div>
    <div class="network-scan-beam"></div>
    <div class="network-aurora network-aurora-gold"></div>
    <div class="network-aurora network-aurora-blue"></div>
    <div class="network-particles" aria-hidden="true">
      <i></i>
      <i></i>
      <i></i>
      <i></i>
      <i></i>
    </div>

    <AppHeader />

    <main class="page-body">
      <!-- 加载状态 -->
      <section
        v-if="pageState === 'loading'"
        class="system-state loading-state"
      >
        <div class="loading-head">
          <div class="skeleton skeleton-logo"></div>

          <div class="loading-copy">
            <div class="skeleton skeleton-title"></div>
            <div class="skeleton skeleton-text"></div>
          </div>
        </div>

        <div class="loading-grid">
          <div
            v-for="index in 4"
            :key="index"
            class="loading-card"
          >
            <div class="skeleton skeleton-label"></div>
            <div class="skeleton skeleton-value"></div>
          </div>
        </div>

        <p class="loading-message">
          正在安全同步当前账户的合作网络数据
        </p>
      </section>

      <!-- 钱包未连接或登录失效 -->
      <section
        v-else-if="pageState === 'wallet'"
        class="system-state wallet-state"
      >
        <div class="state-visual wallet-visual">
          <svg viewBox="0 0 24 24">
            <path
              d="M4.5 5.5h13a2 2 0 0 1 2 2v1.2h-4.2a3.3 3.3 0 0 0 0 6.6h4.2v1.2a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2Z"
            />
            <path
              d="M15.3 10.2h5.2a1 1 0 0 1 1 1v2.6a1 1 0 0 1-1 1h-5.2a2.3 2.3 0 1 1 0-4.6Z"
            />
            <circle cx="16.2" cy="12.5" r=".8" />
          </svg>
        </div>

        <div class="state-badge">
          Secure Account Access
        </div>

        <h1>请先连接钱包</h1>

        <p>
          合作网络数据仅允许当前登录钱包查看。请通过顶部导航连接钱包并完成身份验证，
          系统不会接受前端传入的其他用户地址。
        </p>

        <div class="state-actions">
          <button
            type="button"
            class="primary-button"
            @click="goHome"
          >
            返回首页连接钱包
          </button>

          <button
            type="button"
            class="secondary-button"
            @click="loadTeamOverview"
          >
            重新验证
          </button>
        </div>
      </section>

      <!-- 接口异常 -->
      <section
        v-else-if="pageState === 'error'"
        class="system-state error-state"
      >
        <div class="state-visual error-visual">
          <svg viewBox="0 0 24 24">
            <path
              d="M12 3.2 21 19H3L12 3.2Z"
            />
            <path d="M12 8.5v5.2" />
            <circle cx="12" cy="16.7" r=".8" />
          </svg>
        </div>

        <div class="state-badge error-badge">
          Network Service Notice
        </div>

        <h1>合作网络数据暂时不可用</h1>

        <p>
          {{ errorMessage || '系统暂时无法读取合作网络，请稍后重新尝试。' }}
        </p>

        <div class="state-actions">
          <button
            type="button"
            class="primary-button"
            @click="loadTeamOverview"
          >
            重新加载
          </button>

          <button
            type="button"
            class="secondary-button"
            @click="goHome"
          >
            返回首页
          </button>
        </div>
      </section>

      <template v-else>
        <!-- 用户关系尚未建立 -->
        <section
          v-if="networkNotCreated"
          class="network-notice"
        >
          <div class="notice-icon">
            <span></span>
          </div>

          <div>
            <strong>合作网络尚未建立</strong>

            <p>
              当前账户暂时没有协作关系或有效网络数据。完成关系绑定并建立合作网络后，
              系统将自动生成网络结构与等级进度。
            </p>
          </div>
        </section>

        <!-- 顶部等级 Hero -->
        <section class="hero-premium">
          <div class="hero-grid-bg"></div>
          <div class="hero-glow-gold"></div>
          <div class="hero-glow-blue"></div>
          <div class="hero-scan-line"></div>

          <div class="hero-data-stream" aria-hidden="true">
            <span></span>
            <span></span>
            <span></span>
          </div>

          <div class="level-orbit">
            <div class="orbit-ring ring-a"></div>
            <div class="orbit-ring ring-b"></div>
            <div class="orbit-ring ring-c"></div>

            <div class="orbit-core">
              {{ currentLevel }}
            </div>

            <div class="orbit-dot dot-a"></div>
            <div class="orbit-dot dot-b"></div>
          </div>

          <div class="hero-content">
            <div class="hero-badge">
              <i></i>
              PARTNER NETWORK
            </div>

            <h1>
              BCH 合作网络
              <span>网络结构等级体系</span>
            </h1>

            <p class="hero-slogan">
              当前等级 {{ currentLevel }}
              · 非主导分支规模 {{ money(areaPerformance) }} USDT
            </p>

            <p class="hero-desc">
              网络等级按照“非主导分支配置规模”评估：网络总配置规模扣除主导分支配置规模后，
              其余分支配置规模之和即为非主导分支配置规模。
            </p>

            <div class="hero-level-box">
              <div>
                <span>当前等级</span>
                <strong>{{ currentLevel }}</strong>
              </div>

              <div>
                <span>下一等级</span>
                <strong>{{ nextLevel }}</strong>
              </div>

              <div>
                <span>距离升级</span>
                <strong>
                  {{ money(needMore) }}
                  <small>USDT</small>
                </strong>
              </div>

              <div>
                <span>评估口径</span>
                <strong>非主导分支规模</strong>
              </div>
            </div>
          </div>
        </section>

        <!-- 等级进度 -->
        <section class="section-card">
          <div class="section-head">
            <div>
              <h2>网络等级进度</h2>
              <p>
                B1 至 B5 根据非主导分支配置规模自动调整，等级条件由系统后台统一配置。
              </p>
            </div>

            <span>Network Level</span>
          </div>

          <div
            v-if="levelRules.length"
            class="level-road"
          >
            <div
              v-for="item in levelRules"
              :key="item.level"
              class="level-node"
              :class="{
                active: item.level === currentLevel,
                done: isLevelDone(item)
              }"
            >
              <div class="node-dot">
                {{ item.level }}
              </div>

              <strong>{{ item.level }}</strong>

              <span>
                {{ money(item.require) }}U
              </span>
            </div>
          </div>

          <div
            v-else
            class="inline-empty"
          >
            <div class="inline-empty-icon">
              L
            </div>

            <div>
              <strong>等级规则暂未加载</strong>
              <p>系统尚未返回 B1 至 B5 等级配置。</p>
            </div>
          </div>
        </section>

        <!-- 核心数据 -->
        <section class="stats-grid">
          <div class="stat-card">
            <span>直接合作节点</span>
            <strong>{{ integer(directCount) }}</strong>
            <em>Nodes</em>
          </div>

          <div class="stat-card">
            <span>网络节点总数</span>
            <strong>{{ integer(teamCount) }}</strong>
            <em>Nodes</em>
          </div>

          <div class="stat-card">
            <span>网络总配置规模</span>
            <strong>{{ money(totalTeamPerformance) }}</strong>
            <em>USDT</em>
          </div>

          <div class="stat-card highlight">
            <span>非主导分支规模</span>
            <strong>{{ money(areaPerformance) }}</strong>
            <em>USDT</em>
          </div>
        </section>

        <!-- 小区业绩计算 -->
        <section class="section-card area-card">
          <div class="section-head">
            <div>
              <h2>网络结构规模计算</h2>
              <p>
                非主导分支规模 = 网络总配置规模 - 主导分支配置规模。
              </p>
            </div>

            <span>Structure</span>
          </div>

          <div class="area-calc">
            <div>
              <span>网络总配置规模</span>
              <strong>
                {{ money(totalTeamPerformance) }}
              </strong>
              <em>USDT</em>
            </div>

            <div>
              <span>主导分支配置规模</span>
              <strong>
                {{ money(maxBranchPerformance) }}
              </strong>
              <em>USDT</em>
            </div>

            <div class="result">
              <span>当前非主导分支规模</span>
              <strong>
                {{ money(areaPerformance) }}
              </strong>
              <em>USDT</em>
            </div>
          </div>

          <div class="formula-line">
            <span>
              {{ money(totalTeamPerformance) }}
            </span>

            <i>−</i>

            <span>
              {{ money(maxBranchPerformance) }}
            </span>

            <i>=</i>

            <strong>
              {{ money(areaPerformance) }} USDT
            </strong>
          </div>
        </section>

        <!-- 合作网络分支 -->
        <section class="section-card">
          <div class="section-head">
            <div>
              <h2>合作网络分支</h2>
              <p>
                查看每个直接合作分支的配置规模、等级与主导分支状态。
              </p>
            </div>

            <span>Network</span>
          </div>

          <div
            v-if="sortedBranches.length"
            class="branch-list"
          >
            <div
              v-for="(item, index) in sortedBranches"
              :key="item.address || index"
              class="branch-card"
              :class="{
                max: isMaxBranch(item, index)
              }"
            >
              <div class="branch-top">
                <div class="avatar">
                  {{ item.level }}
                </div>

                <div class="branch-user">
                  <h3>
                    {{ shortAddress(item.address) }}
                  </h3>

                  <p>
                    直接合作分支
                    <span
                      v-if="item.active"
                      class="active-point"
                    >
                      Active
                    </span>
                  </p>
                </div>

                <span
                  v-if="isMaxBranch(item, index)"
                  class="max-badge"
                >
                  主导分支
                </span>
              </div>

              <div class="branch-data">
                <div>
                  <span>网络等级</span>
                  <strong>{{ item.level }}</strong>
                </div>

                <div>
                  <span>网络节点总数</span>
                  <strong>
                    {{ integer(item.teamCount) }}
                  </strong>
                </div>

                <div>
                  <span>节点配置规模</span>
                  <strong>
                    {{ money(item.selfPerformance) }}
                  </strong>
                </div>

                <div>
                  <span>分支配置规模</span>
                  <strong>
                    {{ money(item.teamPerformance) }}
                  </strong>
                </div>
              </div>

              <div class="branch-progress-head">
                <span>规模占比</span>
                <strong>
                  {{ branchPercent(item.teamPerformance) }}%
                </strong>
              </div>

              <div class="branch-bar">
                <div
                  :style="{
                    width:
                      branchPercent(item.teamPerformance) + '%'
                  }"
                ></div>
              </div>
            </div>
          </div>

          <div
            v-else
            class="professional-empty"
          >
            <div class="empty-orbit">
              <div class="empty-ring ring-one"></div>
              <div class="empty-ring ring-two"></div>
              <div class="empty-core">0</div>
            </div>

            <div class="empty-copy">
              <span>NETWORK AWAITING ACTIVATION</span>
              <h3>暂未形成合作分支</h3>

              <p>
                当前账户尚未建立直接合作网络。建立合作关系并形成有效配置规模后，
                每条直接合作分支的等级、节点数量和配置规模将展示在这里。
              </p>
            </div>
          </div>
        </section>

        <!-- 直接合作伙伴 -->
        <section class="section-card">
          <div class="section-head">
            <div>
              <h2>直接合作伙伴</h2>
              <p>
                查看直接合作节点的等级、配置规模与网络贡献。
              </p>
            </div>

            <span>Partners</span>
          </div>

          <div
            v-if="directMembers.length"
            class="member-list"
          >
            <div
              v-for="(item, index) in directMembers"
              :key="item.address || index"
              class="member-card"
            >
              <div class="member-left">
                <div class="member-avatar">
                  {{ item.level }}
                </div>

                <div>
                  <h3>
                    {{ shortAddress(item.address) }}
                  </h3>

                  <p>
                    分支配置规模
                    {{ money(item.teamPerformance) }}
                    USDT
                  </p>
                </div>
              </div>

              <div class="member-right">
                <span
                  class="member-status"
                  :class="{ active: item.active }"
                >
                  {{ item.active ? '有效' : '待激活' }}
                </span>

                <strong>{{ item.level }}</strong>
              </div>
            </div>
          </div>

          <div
            v-else
            class="professional-empty compact-empty"
          >
            <div class="empty-member-icon">
              <span></span>
              <i></i>
            </div>

            <div class="empty-copy">
              <span>DIRECT PARTNER NETWORK</span>
              <h3>暂无直接合作节点</h3>

              <p>
                当前账户还没有直接合作节点，新的协作关系建立后会自动展示。
              </p>
            </div>
          </div>
        </section>
      </template>

      <AppFooter />
    </main>
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import { fetchBchTeamOverview } from '@/api/bchTeam'

export default {
  name: 'TeamNetwork',

  components: {
    AppHeader,
    AppFooter
  },

  data() {
    return {
      /**
       * 页面状态：
       * loading = 加载中
       * ready   = 加载成功
       * wallet  = 钱包未登录或登录失效
       * error   = 接口异常
       */
      pageState: 'loading',

      errorMessage: '',
      networkNotCreated: false,

      userCode: '',

      currentLevel: 'B0',
      currentLevelValue: 0,
      storedLevelValue: 0,

      nextLevel: '--',
      needMore: 0,

      levelRules: [],

      directCount: 0,
      teamCount: 0,

      selfPerformance: 0,
      totalTeamPerformance: 0,
      maxBranchPerformance: 0,
      areaPerformance: 0,

      branches: [],
      directMembers: []
    }
  },

  computed: {
    sortedBranches() {
      return [...this.branches].sort((a, b) => {
        return (
          Number(b.teamPerformance || 0) -
          Number(a.teamPerformance || 0)
        )
      })
    }
  },

  created() {
    this.loadTeamOverview()
  },

  methods: {
    /**
     * 加载当前登录用户团队总览。
     *
     * 前端不传 userCode，
     * 用户地址由后端从当前登录 Token 获取。
     */
    async loadTeamOverview() {
      this.pageState = 'loading'
      this.errorMessage = ''
      this.networkNotCreated = false

      try {
        const response = await fetchBchTeamOverview()
        const data = this.unwrapResponse(response)

        this.applyOverview(data)
        this.pageState = 'ready'
      } catch (error) {
        console.error(
          '[BCH Partner Network] 加载合作网络失败',
          error
        )

        const status = this.extractStatus(error)
        const businessCode = this.extractBusinessCode(error)
        const message = this.extractErrorMessage(error)

        if (
          this.isWalletSessionError(
            status,
            businessCode,
            message
          )
        ) {
          this.resetOverview()
          this.pageState = 'wallet'
          return
        }

        if (this.isNetworkMissingError(message)) {
          this.resetOverview()
          this.networkNotCreated = true
          this.pageState = 'ready'
          return
        }

        this.resetOverview()

        this.errorMessage =
          message ||
          '系统暂时无法读取合作网络数据，请稍后重新尝试。'

        this.pageState = 'error'
      }
    },

    /**
     * 兼容以下几种 apiGet 返回结构：
     *
     * 1. AxiosResponse:
     * {
     *   status: 200,
     *   data: {
     *     code: 0,
     *     data: {}
     *   }
     * }
     *
     * 2. R对象：
     * {
     *   code: 0,
     *   data: {}
     * }
     *
     * 3. 已经被 http.js 解包后的业务对象：
     * {
     *   currentLevel: "B1"
     * }
     */
    unwrapResponse(response) {
      let payload = response

      if (
        payload &&
        typeof payload === 'object' &&
        Object.prototype.hasOwnProperty.call(
          payload,
          'status'
        ) &&
        Object.prototype.hasOwnProperty.call(
          payload,
          'data'
        )
      ) {
        payload = payload.data
      }

      if (
        payload &&
        typeof payload === 'object' &&
        Object.prototype.hasOwnProperty.call(
          payload,
          'code'
        )
      ) {
        const code = Number(payload.code)

        if (code !== 0 && code !== 200) {
          const error = new Error(
            payload.msg ||
              payload.message ||
              '合作网络数据请求失败'
          )

          error.businessCode = payload.code
          throw error
        }

        return payload.data || {}
      }

      return payload || {}
    },

    /**
     * 将接口结果写入页面。
     *
     * 等级、业绩、晋级差额全部以服务端返回为准，
     * 前端只负责格式化展示。
     */
    applyOverview(data) {
      const source = data || {}

      this.userCode = source.userCode || ''

      this.currentLevel =
        source.currentLevel ||
        this.levelName(source.currentLevelValue)

      this.currentLevelValue = this.toInteger(
        source.currentLevelValue
      )

      this.storedLevelValue = this.toInteger(
        source.storedLevelValue
      )

      this.nextLevel = source.nextLevel || '--'
      this.needMore = this.toNumber(source.needMore)

      this.levelRules = Array.isArray(source.levelRules)
        ? source.levelRules
            .map(item => {
              return {
                level:
                  item.level ||
                  this.levelName(item.levelValue),
                levelValue: this.toInteger(
                  item.levelValue
                ),
                require: this.toNumber(item.require)
              }
            })
            .sort((a, b) => {
              return a.levelValue - b.levelValue
            })
        : []

      this.directCount = this.toInteger(
        source.directCount
      )

      this.teamCount = this.toInteger(
        source.teamCount
      )

      this.selfPerformance = this.toNumber(
        source.selfPerformance
      )

      this.totalTeamPerformance = this.toNumber(
        source.totalTeamPerformance
      )

      this.maxBranchPerformance = this.toNumber(
        source.maxBranchPerformance
      )

      this.areaPerformance = this.toNumber(
        source.areaPerformance
      )

      this.branches = Array.isArray(source.branches)
        ? source.branches.map(item =>
            this.normalizeBranch(item)
          )
        : []

      const members = Array.isArray(
        source.directMembers
      )
        ? source.directMembers
        : source.branches

      this.directMembers = Array.isArray(members)
        ? members.map(item =>
            this.normalizeBranch(item)
          )
        : []
    },

    normalizeBranch(item) {
      const source = item || {}

      return {
        address:
          source.address ||
          source.userCode ||
          '',

        level:
          source.level ||
          this.levelName(source.levelValue),

        levelValue: this.toInteger(
          source.levelValue
        ),

        active:
          source.active === true ||
          Number(source.active) === 1 ||
          Number(source.isActive) === 1,

        selfPerformance: this.toNumber(
          source.selfPerformance
        ),

        teamPerformance: this.toNumber(
          source.teamPerformance
        ),

        teamCount: this.toInteger(
          source.teamCount
        ),

        maxBranch:
          source.maxBranch === true ||
          Number(source.maxBranch) === 1
      }
    },

    resetOverview() {
      this.userCode = ''

      this.currentLevel = 'B0'
      this.currentLevelValue = 0
      this.storedLevelValue = 0

      this.nextLevel = '--'
      this.needMore = 0

      this.levelRules = []

      this.directCount = 0
      this.teamCount = 0

      this.selfPerformance = 0
      this.totalTeamPerformance = 0
      this.maxBranchPerformance = 0
      this.areaPerformance = 0

      this.branches = []
      this.directMembers = []
    },

    isLevelDone(item) {
      return (
        Number(this.areaPerformance || 0) >=
        Number(item.require || 0)
      )
    },

    isMaxBranch(item, index) {
      if (item && item.maxBranch === true) {
        return true
      }

      return (
        index === 0 &&
        Number(item.teamPerformance || 0) > 0
      )
    },

    branchPercent(value) {
      const max = Number(
        this.maxBranchPerformance || 0
      )

      if (max <= 0) {
        return 0
      }

      const percent =
        (Number(value || 0) / max) * 100

      return Math.max(
        0,
        Math.min(100, Math.round(percent))
      )
    },

    levelName(value) {
      const levelValue = this.toInteger(value)

      if (levelValue <= 0) {
        return 'B0'
      }

      return `B${levelValue}`
    },

    money(value) {
      const number = Number(value || 0)

      if (!Number.isFinite(number)) {
        return '0'
      }

      return number.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      })
    },

    integer(value) {
      const number = Number(value || 0)

      if (!Number.isFinite(number)) {
        return '0'
      }

      return Math.floor(number).toLocaleString(
        'en-US'
      )
    },

    toNumber(value) {
      const number = Number(value || 0)
      return Number.isFinite(number) ? number : 0
    },

    toInteger(value) {
      const number = Number(value || 0)

      return Number.isFinite(number)
        ? Math.max(0, Math.floor(number))
        : 0
    },

    shortAddress(address) {
      if (!address) {
        return '--'
      }

      const value = String(address)

      if (value.length <= 12) {
        return value
      }

      return `${value.slice(0, 6)}...${value.slice(
        -4
      )}`
    },

    extractStatus(error) {
      if (
        error &&
        error.response &&
        error.response.status
      ) {
        return Number(error.response.status)
      }

      return 0
    },

    extractBusinessCode(error) {
      if (
        error &&
        error.businessCode !== undefined
      ) {
        return Number(error.businessCode)
      }

      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.code !== undefined
      ) {
        return Number(error.response.data.code)
      }

      return 0
    },

    extractErrorMessage(error) {
      if (
        error &&
        error.response &&
        error.response.data
      ) {
        const data = error.response.data

        if (typeof data === 'string') {
          return data
        }

        if (data.msg) {
          return data.msg
        }

        if (data.message) {
          return data.message
        }
      }

      if (error && error.message) {
        return error.message
      }

      return ''
    },

    isWalletSessionError(
      status,
      businessCode,
      message
    ) {
      if (
        status === 401 ||
        status === 403 ||
        businessCode === 401 ||
        businessCode === 403
      ) {
        return true
      }

      const text = String(message || '')
        .trim()
        .toLowerCase()

      return (
        text.includes('未登录') ||
        text.includes('登录失效') ||
        text.includes('token') ||
        text.includes('unauthorized') ||
        text.includes('forbidden') ||
        text.includes('not logged')
      )
    },

    isNetworkMissingError(message) {
      const text = String(message || '').trim()

      return (
        text.includes('用户关系不存在') ||
        text.includes('邀请关系不存在') ||
        text.includes('团队关系不存在')
      )
    },

    goHome() {
      if (
        !this.$router ||
        this.$route.path === '/'
      ) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        })

        return
      }

      this.$router.push('/').catch(() => {})
    }
  }
}
</script>

<style lang="less" scoped>
.team-page {
  min-height: 100vh;
  background:
    radial-gradient(
      circle at 50% -8%,
      rgba(212, 175, 55, 0.18),
      transparent 28%
    ),
    radial-gradient(
      circle at 100% 18%,
      rgba(35, 113, 255, 0.18),
      transparent 32%
    ),
    linear-gradient(
      180deg,
      #05070d 0%,
      #070d18 46%,
      #0b1220 100%
    );
  color: #fff;
}

.page-body {
  padding: 0 12px 28px;
}

/* =========================
   系统状态
   ========================= */

.system-state {
  position: relative;
  overflow: hidden;
  margin-top: 14px;
  min-height: 520px;
  padding: 42px 22px;
  border-radius: 30px;
  text-align: center;
  background:
    radial-gradient(
      circle at 50% 0,
      rgba(212, 175, 55, 0.14),
      transparent 35%
    ),
    linear-gradient(
      180deg,
      rgba(18, 28, 48, 0.94),
      rgba(6, 11, 21, 0.98)
    );
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 24px 70px rgba(0, 0, 0, 0.34);
}

.system-state::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    linear-gradient(
      rgba(255, 255, 255, 0.025) 1px,
      transparent 1px
    ),
    linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.025) 1px,
      transparent 1px
    );
  background-size: 28px 28px;
}

.state-visual {
  position: relative;
  z-index: 2;
  width: 94px;
  height: 94px;
  margin: 48px auto 22px;
  border-radius: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(
      circle at 30% 18%,
      rgba(255, 255, 255, 0.2),
      transparent 25%
    ),
    linear-gradient(
      145deg,
      rgba(212, 175, 55, 0.28),
      rgba(31, 43, 66, 0.92)
    );
  border: 1px solid rgba(212, 175, 55, 0.36);
  box-shadow:
    0 0 44px rgba(212, 175, 55, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
}

.state-visual svg {
  width: 48px;
  height: 48px;
  fill: none;
  stroke: #ffe08a;
  stroke-width: 1.6;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.error-visual {
  background:
    radial-gradient(
      circle at 30% 18%,
      rgba(255, 255, 255, 0.16),
      transparent 25%
    ),
    linear-gradient(
      145deg,
      rgba(239, 68, 68, 0.24),
      rgba(31, 43, 66, 0.92)
    );
  border-color: rgba(248, 113, 113, 0.35);
}

.error-visual svg {
  stroke: #fca5a5;
}

.state-badge {
  position: relative;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  height: 29px;
  padding: 0 13px;
  border-radius: 999px;
  color: #ffe08a;
  background: rgba(212, 175, 55, 0.12);
  border: 1px solid rgba(212, 175, 55, 0.24);
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.7px;
  text-transform: uppercase;
}

.error-badge {
  color: #fca5a5;
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(248, 113, 113, 0.22);
}

.system-state h1 {
  position: relative;
  z-index: 2;
  margin: 19px 0 0;
  font-size: 29px;
  line-height: 1.25;
}

.system-state > p {
  position: relative;
  z-index: 2;
  max-width: 560px;
  margin: 15px auto 0;
  color: #96a9ca;
  font-size: 14px;
  line-height: 1.9;
}

.state-actions {
  position: relative;
  z-index: 2;
  margin-top: 28px;
  display: flex;
  flex-direction: column;
  gap: 11px;
}

.primary-button,
.secondary-button {
  min-height: 48px;
  padding: 0 22px;
  border-radius: 15px;
  border: none;
  font-size: 14px;
  font-weight: 900;
  cursor: pointer;
}

.primary-button {
  color: #080b12;
  background: linear-gradient(
    135deg,
    #d4af37,
    #ffe08a
  );
  box-shadow: 0 13px 30px rgba(212, 175, 55, 0.18);
}

.secondary-button {
  color: #dce8ff;
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(148, 163, 184, 0.18);
}

/* =========================
   加载骨架
   ========================= */

.loading-state {
  text-align: left;
}

.loading-head {
  position: relative;
  z-index: 2;
  margin-top: 36px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.loading-copy {
  flex: 1;
}

.skeleton {
  position: relative;
  overflow: hidden;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.07);
}

.skeleton::after {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.1),
    transparent
  );
  animation: skeletonMove 1.5s infinite;
}

.skeleton-logo {
  width: 68px;
  height: 68px;
  border-radius: 23px;
}

.skeleton-title {
  width: 58%;
  height: 20px;
}

.skeleton-text {
  width: 82%;
  height: 12px;
  margin-top: 13px;
}

.loading-grid {
  position: relative;
  z-index: 2;
  margin-top: 34px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.loading-card {
  padding: 18px 15px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.skeleton-label {
  width: 55%;
  height: 10px;
}

.skeleton-value {
  width: 78%;
  height: 24px;
  margin-top: 18px;
}

.loading-message {
  position: relative;
  z-index: 2;
  margin-top: 28px !important;
  text-align: center;
}

@keyframes skeletonMove {
  100% {
    transform: translateX(100%);
  }
}

/* =========================
   网络未建立提示
   ========================= */

.network-notice {
  margin-top: 14px;
  padding: 15px;
  border-radius: 20px;
  display: flex;
  align-items: flex-start;
  gap: 13px;
  background:
    radial-gradient(
      circle at 0 0,
      rgba(212, 175, 55, 0.16),
      transparent 38%
    ),
    rgba(18, 28, 48, 0.88);
  border: 1px solid rgba(212, 175, 55, 0.22);
}

.notice-icon {
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(212, 175, 55, 0.12);
  border: 1px solid rgba(212, 175, 55, 0.24);
}

.notice-icon span {
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #ffe08a;
  box-shadow: 0 0 18px rgba(255, 224, 138, 0.7);
}

.network-notice strong {
  display: block;
  color: #fff0bd;
  font-size: 14px;
}

.network-notice p {
  margin: 6px 0 0;
  color: #8ea4c8;
  font-size: 12px;
  line-height: 1.7;
}

/* =========================
   Hero
   ========================= */

.hero-premium {
  position: relative;
  overflow: hidden;
  min-height: 520px;
  margin-top: 14px;
  padding: 30px 18px 22px;
  border-radius: 34px;
  background:
    radial-gradient(
      circle at 76% 20%,
      rgba(212, 175, 55, 0.24),
      transparent 28%
    ),
    radial-gradient(
      circle at 20% 80%,
      rgba(37, 99, 235, 0.28),
      transparent 40%
    ),
    linear-gradient(
      145deg,
      #05070d 0%,
      #09111f 50%,
      #111827 100%
    );
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 26px 72px rgba(0, 0, 0, 0.48);
}

.hero-grid-bg {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(
      rgba(255, 255, 255, 0.032) 1px,
      transparent 1px
    ),
    linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.032) 1px,
      transparent 1px
    );
  background-size: 30px 30px;
  opacity: 0.72;
}

.hero-glow-gold,
.hero-glow-blue {
  position: absolute;
  border-radius: 50%;
  filter: blur(48px);
}

.hero-glow-gold {
  width: 220px;
  height: 220px;
  right: -90px;
  top: 80px;
  background: rgba(212, 175, 55, 0.26);
}

.hero-glow-blue {
  width: 240px;
  height: 240px;
  left: -100px;
  bottom: -90px;
  background: rgba(37, 99, 235, 0.26);
}

.hero-content {
  position: relative;
  z-index: 5;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 13px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 900;
  color: #ffe08a;
  background: rgba(212, 175, 55, 0.12);
  border: 1px solid rgba(212, 175, 55, 0.34);
}

.hero-badge i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #facc15;
  box-shadow: 0 0 14px rgba(250, 204, 21, 0.9);
}

.hero-premium h1 {
  margin: 22px 0 0;
  font-size: 39px;
  line-height: 1.08;
  font-weight: 950;
  letter-spacing: -1.5px;
}

.hero-premium h1 span {
  display: block;
  margin-top: 6px;
  background: linear-gradient(
    90deg,
    #d4af37 0%,
    #ffe08a 54%,
    #ffffff 100%
  );
  -webkit-background-clip: text;
  color: transparent;
}

.hero-slogan {
  margin-top: 18px;
  font-size: 15px;
  line-height: 1.85;
  font-weight: 900;
  color: #f5f7ff;
}

.hero-desc {
  margin-top: 12px;
  font-size: 14px;
  line-height: 1.95;
  color: #9fb3d7;
}

.level-orbit {
  position: absolute;
  z-index: 2;
  right: 28px;
  top: 34px;
  width: 142px;
  height: 142px;
  pointer-events: none;
  opacity: 0.92;
}

.orbit-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1px solid rgba(212, 175, 55, 0.26);
}

.ring-a {
  animation: orbitRotate 14s linear infinite;
}

.ring-b {
  inset: 18px;
  border-color: rgba(59, 130, 246, 0.25);
  animation: orbitRotateReverse 10s linear infinite;
}

.ring-c {
  inset: 36px;
  border-color: rgba(255, 255, 255, 0.12);
}

.orbit-core {
  position: absolute;
  inset: 47px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #080b12;
  font-size: 26px;
  font-weight: 950;
  background:
    radial-gradient(
      circle at 28% 20%,
      #fff5c7,
      transparent 25%
    ),
    linear-gradient(
      135deg,
      #d4af37,
      #ffe08a
    );
  box-shadow: 0 0 32px rgba(212, 175, 55, 0.48);
  animation: levelFloat 4.8s ease-in-out infinite;
}

.orbit-dot {
  position: absolute;
  width: 9px;
  height: 9px;
  border-radius: 50%;
}

.dot-a {
  right: 8px;
  top: 44px;
  background: #d4af37;
  box-shadow: 0 0 16px rgba(212, 175, 55, 0.9);
  animation: dotPulse 2.6s ease-in-out infinite;
}

.dot-b {
  left: 18px;
  bottom: 30px;
  background: #3b82f6;
  box-shadow: 0 0 16px rgba(59, 130, 246, 0.9);
  animation: dotPulse 2.6s ease-in-out infinite 0.8s;
}

@keyframes orbitRotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@keyframes orbitRotateReverse {
  from {
    transform: rotate(360deg);
  }

  to {
    transform: rotate(0deg);
  }
}

@keyframes levelFloat {
  0%,
  100% {
    transform: translateY(0) scale(1);
  }

  50% {
    transform: translateY(-7px) scale(1.04);
  }
}

@keyframes dotPulse {
  0%,
  100% {
    transform: scale(0.9);
    opacity: 0.6;
  }

  50% {
    transform: scale(1.25);
    opacity: 1;
  }
}

.hero-level-box {
  margin-top: 22px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 11px;
}

.hero-level-box div,
.section-card,
.stat-card,
.branch-card,
.member-card {
  background: linear-gradient(
    180deg,
    rgba(18, 28, 48, 0.88),
    rgba(8, 14, 26, 0.92)
  );
  border: 1px solid rgba(148, 163, 184, 0.15);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 18px 48px rgba(0, 0, 0, 0.26);
}

.hero-level-box div {
  padding: 14px 13px;
  border-radius: 18px;
}

.hero-level-box span,
.stat-card span,
.area-calc span,
.branch-data span {
  display: block;
  font-size: 12px;
  color: #8ea4c8;
}

.hero-level-box strong {
  display: block;
  margin-top: 7px;
  font-size: 19px;
  color: #fff;
}

.hero-level-box small {
  margin-left: 3px;
  color: #ffe08a;
  font-size: 10px;
}

/* =========================
   通用区块
   ========================= */

.section-card {
  margin-top: 14px;
  border-radius: 28px;
  padding: 18px 14px;
}

.section-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.section-head h2 {
  margin: 0;
  font-size: 22px;
}

.section-head p {
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.7;
  color: #8ea4c8;
}

.section-head > span {
  height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  color: #ffe08a;
  background: rgba(212, 175, 55, 0.12);
  font-size: 11px;
  font-weight: 950;
}

/* =========================
   等级
   ========================= */

.level-road {
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
}

.level-node {
  position: relative;
  text-align: center;
}

.node-dot {
  width: 42px;
  height: 42px;
  margin: 0 auto 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #8ea4c8;
  font-size: 13px;
  font-weight: 950;
}

.level-node.done .node-dot {
  color: #080b12;
  background: linear-gradient(
    135deg,
    #d4af37,
    #ffe08a
  );
  box-shadow: 0 0 24px rgba(212, 175, 55, 0.28);
}

.level-node.active .node-dot {
  transform: scale(1.12);
  box-shadow:
    0 0 0 4px rgba(212, 175, 55, 0.1),
    0 0 28px rgba(212, 175, 55, 0.36);
}

.level-node strong {
  display: block;
  font-size: 13px;
}

.level-node span {
  display: block;
  margin-top: 5px;
  font-size: 10px;
  color: #8ea4c8;
}

/* =========================
   统计
   ========================= */

.stats-grid {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.stat-card {
  border-radius: 22px;
  padding: 16px 14px;
}

.stat-card strong {
  display: block;
  margin-top: 12px;
  overflow-wrap: anywhere;
  font-size: 25px;
}

.stat-card em {
  display: block;
  margin-top: 7px;
  color: #ffe08a;
  font-style: normal;
  font-size: 12px;
}

.stat-card.highlight {
  background:
    radial-gradient(
      circle at 100% 0,
      rgba(212, 175, 55, 0.22),
      transparent 42%
    ),
    linear-gradient(
      180deg,
      rgba(30, 25, 12, 0.92),
      rgba(13, 25, 44, 0.88)
    );
}

/* =========================
   网络结构计算
   ========================= */

.area-calc {
  margin-top: 16px;
  display: grid;
  gap: 12px;
}

.area-calc div {
  padding: 16px 14px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(255, 255, 255, 0.07);
}

.area-calc .result {
  background: rgba(212, 175, 55, 0.12);
  border-color: rgba(212, 175, 55, 0.3);
}

.area-calc strong {
  display: block;
  margin-top: 8px;
  overflow-wrap: anywhere;
  font-size: 24px;
}

.area-calc em {
  display: block;
  margin-top: 5px;
  color: #ffe08a;
  font-style: normal;
  font-size: 12px;
}

.formula-line {
  margin-top: 14px;
  padding: 13px 12px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  background: rgba(255, 255, 255, 0.035);
  border: 1px dashed rgba(148, 163, 184, 0.17);
  color: #8ea4c8;
  font-size: 12px;
}

.formula-line i {
  color: #586b8c;
  font-style: normal;
}

.formula-line strong {
  color: #ffe08a;
}

/* =========================
   合作网络分支
   ========================= */

.branch-list,
.member-list {
  margin-top: 16px;
  display: grid;
  gap: 14px;
}

.branch-card,
.member-card {
  border-radius: 24px;
  padding: 16px 14px;
}

.branch-card.max {
  background:
    radial-gradient(
      circle at 100% 0,
      rgba(212, 175, 55, 0.18),
      transparent 38%
    ),
    linear-gradient(
      180deg,
      rgba(18, 28, 48, 0.92),
      rgba(8, 14, 26, 0.92)
    );
  border-color: rgba(212, 175, 55, 0.22);
}

.branch-top {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar,
.member-avatar {
  width: 44px;
  height: 44px;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    #d4af37,
    #ffe08a
  );
  color: #080b12;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 950;
  flex-shrink: 0;
}

.branch-user {
  min-width: 0;
}

.branch-top h3,
.member-card h3 {
  margin: 0;
  overflow-wrap: anywhere;
  font-size: 16px;
}

.branch-top p,
.member-card p {
  margin: 6px 0 0;
  font-size: 12px;
  color: #8ea4c8;
}

.active-point {
  margin-left: 5px;
  color: #6ee7b7;
  font-size: 10px;
  font-weight: 800;
}

.max-badge {
  margin-left: auto;
  height: 28px;
  padding: 0 10px;
  flex-shrink: 0;
  border-radius: 999px;
  display: flex;
  align-items: center;
  color: #ffe08a;
  background: rgba(212, 175, 55, 0.12);
  font-size: 12px;
  font-weight: 900;
}

.branch-data {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.branch-data div {
  min-width: 0;
  padding: 13px 12px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.055);
}

.branch-data strong {
  display: block;
  margin-top: 8px;
  overflow-wrap: anywhere;
  font-size: 17px;
}

.branch-progress-head {
  margin-top: 14px;
  display: flex;
  justify-content: space-between;
  color: #7186a9;
  font-size: 11px;
}

.branch-progress-head strong {
  color: #ffe08a;
}

.branch-bar {
  margin-top: 8px;
  height: 9px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.08);
}

.branch-bar div {
  height: 100%;
  min-width: 0;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    #d4af37,
    #ffe08a
  );
  transition: width 0.5s ease;
}

/* =========================
   直接合作伙伴
   ========================= */

.member-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.member-left {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.member-right {
  flex-shrink: 0;
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  gap: 7px;
}

.member-right > strong {
  min-width: 42px;
  height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffe08a;
  background: rgba(212, 175, 55, 0.12);
  font-size: 12px;
}

.member-status {
  color: #7589aa;
  font-size: 10px;
}

.member-status.active {
  color: #6ee7b7;
}

/* =========================
   专业空状态
   ========================= */

.professional-empty {
  margin-top: 17px;
  min-height: 260px;
  padding: 28px 20px;
  border-radius: 24px;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  background:
    radial-gradient(
      circle at 50% 0,
      rgba(59, 130, 246, 0.09),
      transparent 40%
    ),
    rgba(255, 255, 255, 0.025);
  border: 1px dashed rgba(148, 163, 184, 0.16);
}

.empty-orbit {
  position: relative;
  width: 88px;
  height: 88px;
}

.empty-ring {
  position: absolute;
  border-radius: 50%;
  border: 1px solid rgba(148, 163, 184, 0.16);
}

.ring-one {
  inset: 0;
}

.ring-two {
  inset: 15px;
  border-color: rgba(212, 175, 55, 0.21);
}

.empty-core {
  position: absolute;
  inset: 29px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7186a9;
  background: rgba(255, 255, 255, 0.055);
  font-size: 15px;
  font-weight: 900;
}

.empty-copy {
  margin-top: 19px;
}

.empty-copy > span {
  color: #d4af37;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 1.2px;
  text-transform: uppercase;
}

.empty-copy h3 {
  margin: 8px 0 0;
  font-size: 19px;
}

.empty-copy p {
  max-width: 470px;
  margin: 10px auto 0;
  color: #8094b5;
  font-size: 12px;
  line-height: 1.8;
}

.compact-empty {
  min-height: 210px;
}

.empty-member-icon {
  position: relative;
  width: 70px;
  height: 56px;
}

.empty-member-icon span,
.empty-member-icon i {
  position: absolute;
  border-radius: 50%;
  background: rgba(148, 163, 184, 0.11);
  border: 1px solid rgba(148, 163, 184, 0.18);
}

.empty-member-icon span {
  width: 34px;
  height: 34px;
  left: 6px;
  top: 3px;
}

.empty-member-icon i {
  width: 27px;
  height: 27px;
  right: 7px;
  bottom: 0;
}

.inline-empty {
  margin-top: 17px;
  padding: 17px 15px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  gap: 13px;
  background: rgba(255, 255, 255, 0.035);
  border: 1px dashed rgba(148, 163, 184, 0.15);
}

.inline-empty-icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffe08a;
  background: rgba(212, 175, 55, 0.1);
  font-weight: 900;
}

.inline-empty strong {
  font-size: 13px;
}

.inline-empty p {
  margin: 5px 0 0;
  color: #7e92b3;
  font-size: 11px;
}

/* =========================
   桌面端
   ========================= */

@media (min-width: 768px) {
  .page-body {
    max-width: 1180px;
    margin: 0 auto;
  }

  .hero-premium {
    padding: 52px 44px 36px;
    min-height: 620px;
  }

  .hero-premium h1 {
    font-size: 62px;
  }

  .level-orbit {
    width: 220px;
    height: 220px;
    right: 70px;
    top: 64px;
  }

  .orbit-core {
    inset: 74px;
    font-size: 40px;
  }

  .ring-b {
    inset: 28px;
  }

  .ring-c {
    inset: 56px;
  }

  .hero-level-box,
  .stats-grid {
    grid-template-columns: repeat(4, 1fr);
  }

  .area-calc {
    grid-template-columns: repeat(3, 1fr);
  }

  .branch-list {
    grid-template-columns: repeat(2, 1fr);
  }

  .state-actions {
    align-items: center;
    justify-content: center;
    flex-direction: row;
  }

  .primary-button,
  .secondary-button {
    min-width: 180px;
  }

  .loading-grid {
    grid-template-columns: repeat(4, 1fr);
  }

  .professional-empty {
    flex-direction: row;
    gap: 30px;
    text-align: left;
  }

  .empty-copy {
    margin-top: 0;
  }

  .empty-copy p {
    margin-left: 0;
    margin-right: 0;
  }
}
/* =========================================================
 * BCH PARTNER NETWORK — HOME VISUAL SYSTEM
 * 首页同款：深蓝黑 + 科技蓝 + 低饱和金色
 * ========================================================= */

.team-page {
  position: relative;
  isolation: isolate;
  overflow-x: hidden;

  background:
    radial-gradient(
      circle at 50% -10%,
      rgba(214, 177, 63, .18),
      transparent 26%
    ),
    radial-gradient(
      circle at 105% 22%,
      rgba(41, 108, 255, .18),
      transparent 31%
    ),
    radial-gradient(
      circle at -8% 68%,
      rgba(22, 78, 164, .13),
      transparent 30%
    ),
    linear-gradient(
      180deg,
      #03060c 0%,
      #07101d 42%,
      #091321 100%
    );
}

/* 全局科技网格 */
.network-grid-layer {
  position: fixed;
  z-index: -5;
  inset: 0;

  opacity: .34;
  pointer-events: none;

  background-image:
    linear-gradient(
      rgba(118, 151, 201, .032) 1px,
      transparent 1px
    ),
    linear-gradient(
      90deg,
      rgba(118, 151, 201, .032) 1px,
      transparent 1px
    );

  background-size: 36px 36px;

  -webkit-mask-image:
    linear-gradient(
      180deg,
      #000 0%,
      rgba(0, 0, 0, .78) 45%,
      transparent 100%
    );

  mask-image:
    linear-gradient(
      180deg,
      #000 0%,
      rgba(0, 0, 0, .78) 45%,
      transparent 100%
    );

  animation:
    networkGridMove 28s linear infinite;
}

/* 页面扫描光 */
.network-scan-beam {
  position: fixed;
  z-index: -2;

  top: -22%;
  bottom: -22%;
  left: -190px;

  width: 150px;

  transform: skewX(-20deg);

  background: linear-gradient(
    90deg,
    transparent,
    rgba(75, 143, 255, .035),
    rgba(255, 224, 138, .055),
    rgba(255, 255, 255, .04),
    transparent
  );

  pointer-events: none;

  animation:
    networkBeamSweep 12s ease-in-out infinite;
}

/* 蓝金环境光 */
.network-aurora {
  position: fixed;
  z-index: -4;

  border-radius: 50%;
  filter: blur(82px);

  pointer-events: none;
}

.network-aurora-gold {
  left: -180px;
  bottom: 10%;

  width: 330px;
  height: 330px;

  background:
    rgba(214, 177, 63, .11);

  animation:
    networkAurora 13s ease-in-out infinite reverse;
}

.network-aurora-blue {
  top: 16%;
  right: -170px;

  width: 320px;
  height: 320px;

  background:
    rgba(41, 108, 255, .16);

  animation:
    networkAurora 11s ease-in-out infinite;
}

/* 漂浮微粒 */
.network-particles {
  position: fixed;
  z-index: -1;
  inset: 0;

  pointer-events: none;
}

.network-particles i {
  position: absolute;

  width: 3px;
  height: 3px;

  border-radius: 50%;

  background: #60a5fa;

  box-shadow:
    0 0 9px rgba(96, 165, 250, .86),
    0 0 17px rgba(59, 130, 246, .32);

  animation:
    networkParticleFloat 6.5s ease-in-out infinite;
}

.network-particles i:nth-child(1) {
  top: 15%;
  left: 7%;
}

.network-particles i:nth-child(2) {
  top: 33%;
  right: 8%;

  background: #ffe08a;

  box-shadow:
    0 0 9px rgba(255, 224, 138, .88),
    0 0 17px rgba(212, 175, 55, .3);

  animation-delay: 1.3s;
}

.network-particles i:nth-child(3) {
  top: 59%;
  left: 13%;

  background: #93c5fd;

  animation-delay: 2.5s;
}

.network-particles i:nth-child(4) {
  right: 12%;
  bottom: 18%;

  background: #d4af37;

  animation-delay: 3.7s;
}

.network-particles i:nth-child(5) {
  left: 48%;
  bottom: 7%;

  width: 2px;
  height: 2px;

  animation-delay: 4.5s;
}

.page-body {
  position: relative;
  z-index: 1;
}

/* =========================================================
 * 状态页
 * ========================================================= */

.system-state {
  isolation: isolate;

  border-color:
    rgba(137, 163, 206, .15);

  background:
    radial-gradient(
      circle at 50% 0%,
      rgba(214, 177, 63, .14),
      transparent 34%
    ),
    radial-gradient(
      circle at 0% 100%,
      rgba(37, 99, 235, .1),
      transparent 38%
    ),
    linear-gradient(
      180deg,
      rgba(17, 28, 48, .94),
      rgba(7, 14, 26, .98)
    );

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, .055),
    0 26px 70px rgba(0, 0, 0, .38);

  animation:
    networkSectionReveal .72s
    cubic-bezier(.2, .75, .25, 1)
    both;
}

.system-state::after {
  position: absolute;

  top: 0;
  left: 10%;
  right: 10%;

  height: 1px;

  content: "";

  background: linear-gradient(
    90deg,
    transparent,
    rgba(77, 145, 255, .56),
    rgba(255, 224, 138, .58),
    transparent
  );

  background-size: 200% 100%;

  animation:
    networkBorderFlow 7s linear infinite;
}

.state-visual {
  animation:
    networkVisualFloat 4.8s ease-in-out infinite;
}

.primary-button {
  position: relative;
  overflow: hidden;
  isolation: isolate;

  border:
    1px solid rgba(255, 243, 194, .58);

  background:
    linear-gradient(
      135deg,
      #bd9222 0%,
      #e0bc55 38%,
      #ffe49a 66%,
      #c79c2b 100%
    );

  background-size: 180% 180%;

  box-shadow:
    0 16px 36px rgba(197, 151, 34, .24),
    inset 0 1px 0 rgba(255, 255, 255, .62);

  transition:
    transform .2s ease,
    box-shadow .2s ease;
}

.primary-button::before {
  position: absolute;
  z-index: -1;

  top: -18px;
  bottom: -18px;
  left: -54px;

  width: 36px;

  content: "";
  transform: skewX(-22deg);

  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, .68),
    transparent
  );

  animation:
    networkButtonSweep 5.2s ease-in-out infinite;
}

.primary-button:hover {
  transform: translateY(-2px);

  box-shadow:
    0 21px 44px rgba(197, 151, 34, .3),
    inset 0 1px 0 rgba(255, 255, 255, .72);
}

.secondary-button {
  transition:
    transform .2s ease,
    border-color .2s ease,
    background .2s ease;
}

.secondary-button:hover {
  transform: translateY(-2px);

  border-color:
    rgba(100, 153, 236, .25);

  background:
    rgba(37, 99, 235, .07);
}

/* =========================================================
 * 网络提示
 * ========================================================= */

.network-notice {
  position: relative;
  overflow: hidden;

  border-color:
    rgba(212, 175, 55, .22);

  background:
    radial-gradient(
      circle at 0% 0%,
      rgba(212, 175, 55, .15),
      transparent 38%
    ),
    radial-gradient(
      circle at 100% 100%,
      rgba(37, 99, 235, .08),
      transparent 38%
    ),
    linear-gradient(
      145deg,
      rgba(17, 28, 48, .92),
      rgba(7, 14, 26, .95)
    );

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, .05),
    0 15px 36px rgba(0, 0, 0, .2);
}

.network-notice::after {
  position: absolute;

  top: -20px;
  bottom: -20px;
  left: -46px;

  width: 29px;

  content: "";
  transform: skewX(-22deg);

  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 224, 138, .12),
    transparent
  );

  animation:
    networkNoticeSweep 7s ease-in-out infinite;
}

/* =========================================================
 * HERO：与首页统一
 * ========================================================= */

.hero-premium {
  isolation: isolate;

  border-color:
    rgba(151, 174, 214, .13);

  background:
    radial-gradient(
      circle at 80% 17%,
      rgba(214, 177, 63, .22),
      transparent 25%
    ),
    radial-gradient(
      circle at 11% 84%,
      rgba(36, 101, 226, .25),
      transparent 38%
    ),
    linear-gradient(
      148deg,
      #03060c 0%,
      #08111f 48%,
      #101a2a 100%
    );

  box-shadow:
    0 34px 90px rgba(0, 0, 0, .52),
    inset 0 1px 0 rgba(255, 255, 255, .055),
    inset 0 -1px 0 rgba(60, 130, 255, .08);

  animation:
    networkSectionReveal .72s
    cubic-bezier(.2, .75, .25, 1)
    both;
}

.hero-premium::before {
  position: absolute;
  z-index: 1;

  top: -35%;
  bottom: -35%;
  left: -32%;

  width: 24%;

  content: "";
  transform: skewX(-19deg);
  pointer-events: none;

  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, .02),
    rgba(255, 226, 145, .08),
    rgba(255, 255, 255, .025),
    transparent
  );

  animation:
    networkHeroSweep 8.8s ease-in-out infinite;
}

.hero-premium::after {
  position: absolute;
  z-index: 3;

  top: 0;
  left: 8%;
  right: 8%;

  height: 1px;

  content: "";
  pointer-events: none;

  background: linear-gradient(
    90deg,
    transparent,
    rgba(75, 143, 255, .55),
    rgba(255, 224, 138, .72),
    rgba(75, 143, 255, .55),
    transparent
  );

  background-size: 220% 100%;

  animation:
    networkBorderFlow 5.6s linear infinite;
}

.hero-grid-bg {
  animation:
    networkHeroGrid 24s linear infinite;
}

.hero-glow-gold {
  animation:
    networkGlowGold 7s ease-in-out infinite;
}

.hero-glow-blue {
  animation:
    networkGlowBlue 9s ease-in-out infinite;
}

.hero-scan-line {
  position: absolute;
  z-index: 2;

  top: -28%;
  bottom: -28%;
  left: -110px;

  width: 94px;

  transform: skewX(-22deg);

  background: linear-gradient(
    90deg,
    transparent,
    rgba(59, 130, 246, .045),
    rgba(255, 224, 138, .07),
    rgba(255, 255, 255, .06),
    transparent
  );

  animation:
    networkHeroLine 9s ease-in-out infinite;
}

.hero-data-stream {
  position: absolute;
  z-index: 1;

  right: 3%;
  bottom: 7%;

  width: 46%;
  height: 90px;

  opacity: .3;
  pointer-events: none;
}

.hero-data-stream span {
  position: absolute;
  left: 0;
  right: 0;

  height: 1px;

  background: linear-gradient(
    90deg,
    transparent,
    rgba(75, 143, 255, .7),
    rgba(255, 224, 138, .58),
    transparent
  );

  transform-origin: left center;

  animation:
    networkDataPulse 3.6s ease-in-out infinite;
}

.hero-data-stream span:nth-child(1) {
  top: 16%;
  transform: rotate(-5deg);
}

.hero-data-stream span:nth-child(2) {
  top: 49%;
  transform: rotate(4deg);
  animation-delay: .8s;
}

.hero-data-stream span:nth-child(3) {
  top: 78%;
  transform: rotate(-2deg);
  animation-delay: 1.5s;
}

.hero-content {
  animation:
    networkContentRise .76s .06s
    cubic-bezier(.2, .75, .25, 1)
    both;
}

.hero-badge {
  position: relative;
  overflow: hidden;

  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, .08),
    0 10px 28px rgba(0, 0, 0, .16);
}

.hero-badge::after {
  position: absolute;

  top: 0;
  bottom: 0;
  left: -42px;

  width: 28px;

  content: "";
  transform: skewX(-22deg);

  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, .3),
    transparent
  );

  animation:
    networkBadgeSweep 5.5s ease-in-out infinite;
}

.hero-badge i {
  animation:
    networkStatusPulse 1.9s ease-in-out infinite;
}

.hero-premium h1 span {
  background-size: 200% 100%;

  animation:
    networkTextFlow 6.5s linear infinite;
}

.level-orbit {
  filter:
    drop-shadow(0 0 24px rgba(37, 99, 235, .08));
}

.orbit-core {
  box-shadow:
    0 0 12px rgba(255, 224, 138, .8),
    0 0 32px rgba(212, 175, 55, .48),
    0 0 60px rgba(212, 175, 55, .2);
}

/* Hero 资产指标 */
.hero-level-box div {
  position: relative;
  overflow: hidden;

  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);

  border-color:
    rgba(137, 163, 206, .13);

  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(43, 104, 209, .07),
      transparent 38%
    ),
    linear-gradient(
      145deg,
      rgba(255, 255, 255, .052),
      rgba(255, 255, 255, .02)
    );

  transition:
    transform .22s ease,
    border-color .22s ease,
    box-shadow .22s ease;
}

.hero-level-box div::before {
  position: absolute;

  top: 0;
  left: 14%;
  right: 14%;

  height: 1px;

  content: "";

  background: linear-gradient(
    90deg,
    transparent,
    rgba(75, 143, 255, .48),
    rgba(255, 224, 138, .38),
    transparent
  );
}

.hero-level-box div:hover {
  transform: translateY(-3px);

  border-color:
    rgba(100, 153, 236, .27);

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, .07),
    0 18px 38px rgba(0, 0, 0, .28),
    0 0 26px rgba(38, 101, 206, .06);
}

/* =========================================================
 * 通用卡片体系
 * ========================================================= */

.section-card,
.stat-card,
.branch-card,
.member-card {
  position: relative;
  overflow: hidden;

  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);

  border-color:
    rgba(137, 163, 206, .15);

  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(43, 104, 209, .06),
      transparent 34%
    ),
    linear-gradient(
      180deg,
      rgba(17, 28, 48, .9),
      rgba(7, 14, 26, .94)
    );

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, .055),
    0 20px 52px rgba(0, 0, 0, .28);

  transition:
    transform .24s ease,
    border-color .24s ease,
    box-shadow .24s ease;
}

.section-card::before,
.stat-card::before,
.branch-card::before,
.member-card::before {
  position: absolute;
  z-index: 0;

  top: 0;
  left: 10%;
  right: 10%;

  height: 1px;

  content: "";
  opacity: .58;

  background: linear-gradient(
    90deg,
    transparent,
    rgba(77, 145, 255, .56),
    rgba(255, 224, 138, .48),
    transparent
  );

  background-size: 200% 100%;

  animation:
    networkBorderFlow 7s linear infinite;
}

.section-card > *,
.stat-card > *,
.branch-card > *,
.member-card > * {
  position: relative;
  z-index: 1;
}

.stat-card:hover,
.branch-card:hover,
.member-card:hover {
  transform: translateY(-4px);

  border-color:
    rgba(100, 153, 236, .28);

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, .075),
    0 24px 58px rgba(0, 0, 0, .36),
    0 0 28px rgba(38, 101, 206, .07);
}

.section-head > span {
  font-family:
    SFMono-Regular,
    Consolas,
    "Liberation Mono",
    monospace;

  letter-spacing: .35px;
}

/* =========================================================
 * 网络等级
 * ========================================================= */

.level-road {
  position: relative;
}

.level-road::before {
  position: absolute;

  top: 21px;
  left: 9%;
  right: 9%;

  height: 1px;

  content: "";

  background:
    rgba(123, 157, 209, .12);
}

.level-road::after {
  position: absolute;

  top: 21px;
  left: 9%;

  width: 42%;
  height: 1px;

  content: "";

  background: linear-gradient(
    90deg,
    transparent,
    #3b82f6,
    #ffe08a,
    transparent
  );

  animation:
    networkLevelFlow 3.2s linear infinite;
}

.level-node {
  z-index: 2;
}

.node-dot {
  position: relative;
  z-index: 2;

  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.level-node.done .node-dot,
.level-node.active .node-dot {
  animation:
    networkNodePulse 2.8s ease-in-out infinite;
}

/* =========================================================
 * 统计数据
 * ========================================================= */

.stat-card strong,
.area-calc strong,
.branch-data strong,
.hero-level-box strong {
  font-variant-numeric: tabular-nums;
}

.stat-card.highlight {
  border-color:
    rgba(215, 181, 76, .2);

  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(215, 181, 76, .19),
      transparent 43%
    ),
    radial-gradient(
      circle at 0% 100%,
      rgba(36, 101, 226, .11),
      transparent 44%
    ),
    linear-gradient(
      180deg,
      rgba(29, 26, 17, .93),
      rgba(10, 21, 38, .92)
    );
}

.stat-card.highlight strong {
  color: #ffe39a;

  text-shadow:
    0 0 24px rgba(215, 181, 76, .16);
}

/* =========================================================
 * 网络结构规模计算
 * ========================================================= */

.area-card {
  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(214, 177, 63, .1),
      transparent 34%
    ),
    radial-gradient(
      circle at 0% 100%,
      rgba(37, 99, 235, .08),
      transparent 38%
    ),
    linear-gradient(
      180deg,
      rgba(17, 28, 48, .92),
      rgba(7, 14, 26, .95)
    );
}

.area-calc div {
  position: relative;
  overflow: hidden;

  background:
    linear-gradient(
      145deg,
      rgba(255, 255, 255, .052),
      rgba(255, 255, 255, .023)
    );

  border:
    1px solid rgba(137, 163, 206, .11);

  transition:
    transform .22s ease,
    border-color .22s ease;
}

.area-calc div:hover {
  transform: translateY(-2px);

  border-color:
    rgba(100, 153, 236, .24);
}

.area-calc .result {
  border-color:
    rgba(212, 175, 55, .3);

  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(212, 175, 55, .17),
      transparent 46%
    ),
    linear-gradient(
      145deg,
      rgba(212, 175, 55, .1),
      rgba(37, 99, 235, .04)
    );
}

.formula-line {
  position: relative;
  overflow: hidden;

  background:
    linear-gradient(
      145deg,
      rgba(37, 99, 235, .045),
      rgba(255, 255, 255, .02)
    );

  border-color:
    rgba(123, 157, 209, .18);
}

.formula-line::after {
  position: absolute;

  top: 0;
  bottom: 0;
  left: -50px;

  width: 30px;

  content: "";
  transform: skewX(-22deg);

  background: linear-gradient(
    90deg,
    transparent,
    rgba(103, 164, 255, .14),
    transparent
  );

  animation:
    networkFormulaSweep 7.2s ease-in-out infinite;
}

/* =========================================================
 * 分支卡片
 * ========================================================= */

.branch-card.max {
  border-color:
    rgba(212, 175, 55, .25);

  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(212, 175, 55, .18),
      transparent 38%
    ),
    radial-gradient(
      circle at 0% 100%,
      rgba(37, 99, 235, .08),
      transparent 38%
    ),
    linear-gradient(
      180deg,
      rgba(18, 28, 48, .94),
      rgba(8, 14, 26, .95)
    );
}

.avatar,
.member-avatar {
  position: relative;
  overflow: hidden;

  box-shadow:
    0 12px 26px rgba(212, 175, 55, .18),
    inset 0 1px 0 rgba(255, 255, 255, .42);
}

.avatar::after,
.member-avatar::after {
  position: absolute;

  top: -16px;
  bottom: -16px;
  left: -38px;

  width: 24px;

  content: "";
  transform: skewX(-22deg);

  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, .62),
    transparent
  );

  animation:
    networkAvatarSweep 6.5s ease-in-out infinite;
}

.branch-data div {
  border:
    1px solid rgba(137, 163, 206, .1);

  background:
    linear-gradient(
      145deg,
      rgba(255, 255, 255, .048),
      rgba(255, 255, 255, .02)
    );
}

.branch-bar {
  position: relative;
}

.branch-bar::after {
  position: absolute;
  inset: 0;

  content: "";

  background: linear-gradient(
    110deg,
    transparent 0%,
    rgba(255, 255, 255, .2) 48%,
    transparent 56%
  );

  background-size: 44px 100%;

  animation:
    networkProgressFlow 2.8s linear infinite;

  pointer-events: none;
}

.branch-bar div {
  background: linear-gradient(
    90deg,
    #3b82f6 0%,
    #d4af37 55%,
    #ffe08a 100%
  );

  box-shadow:
    0 0 14px rgba(212, 175, 55, .18);
}

/* =========================================================
 * 直接合作伙伴
 * ========================================================= */

.member-card {
  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(43, 104, 209, .07),
      transparent 34%
    ),
    linear-gradient(
      180deg,
      rgba(17, 28, 48, .91),
      rgba(7, 14, 26, .95)
    );
}

.member-status.active {
  color: #75f3a3;

  text-shadow:
    0 0 12px rgba(34, 197, 94, .22);
}

/* =========================================================
 * 空状态
 * ========================================================= */

.professional-empty,
.inline-empty {
  position: relative;
  overflow: hidden;

  background:
    radial-gradient(
      circle at 50% 0%,
      rgba(59, 130, 246, .09),
      transparent 40%
    ),
    linear-gradient(
      145deg,
      rgba(255, 255, 255, .032),
      rgba(255, 255, 255, .014)
    );

  border-color:
    rgba(123, 157, 209, .16);
}

.empty-ring {
  animation:
    networkEmptyOrbit 9s linear infinite;
}

.ring-two {
  animation-direction: reverse;
  animation-duration: 12s;
}

.empty-core {
  box-shadow:
    0 0 22px rgba(37, 99, 235, .08);
}

/* =========================================================
 * 动画
 * ========================================================= */

@keyframes networkGridMove {
  from {
    background-position:
      0 0,
      0 0;
  }

  to {
    background-position:
      36px 36px,
      36px 36px;
  }
}

@keyframes networkBeamSweep {
  0%,
  18% {
    left: -200px;
    opacity: 0;
  }

  30% {
    opacity: .7;
  }

  68% {
    left: calc(100% + 190px);
    opacity: .2;
  }

  100% {
    left: calc(100% + 190px);
    opacity: 0;
  }
}

@keyframes networkAurora {
  0%,
  100% {
    opacity: .48;

    transform:
      translate3d(0, 0, 0)
      scale(.9);
  }

  50% {
    opacity: .92;

    transform:
      translate3d(18px, -18px, 0)
      scale(1.08);
  }
}

@keyframes networkParticleFloat {
  0%,
  100% {
    opacity: .2;

    transform:
      translateY(9px)
      scale(.75);
  }

  50% {
    opacity: 1;

    transform:
      translateY(-12px)
      scale(1.18);
  }
}

@keyframes networkSectionReveal {
  from {
    opacity: 0;

    transform:
      translateY(18px)
      scale(.988);
  }

  to {
    opacity: 1;

    transform:
      translateY(0)
      scale(1);
  }
}

@keyframes networkVisualFloat {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-6px);
  }
}

@keyframes networkBorderFlow {
  from {
    background-position: 0% 50%;
  }

  to {
    background-position: 220% 50%;
  }
}

@keyframes networkButtonSweep {
  0%,
  20% {
    left: -54px;
    opacity: 0;
  }

  32% {
    opacity: 1;
  }

  54% {
    left: calc(100% + 54px);
    opacity: 0;
  }

  100% {
    left: calc(100% + 54px);
    opacity: 0;
  }
}

@keyframes networkNoticeSweep {
  0%,
  18% {
    left: -46px;
    opacity: 0;
  }

  30% {
    opacity: 1;
  }

  52% {
    left: calc(100% + 46px);
    opacity: 0;
  }

  100% {
    left: calc(100% + 46px);
    opacity: 0;
  }
}

@keyframes networkHeroSweep {
  0%,
  15% {
    left: -32%;
    opacity: 0;
  }

  25% {
    opacity: .9;
  }

  52% {
    left: 115%;
    opacity: 0;
  }

  100% {
    left: 115%;
    opacity: 0;
  }
}

@keyframes networkHeroGrid {
  from {
    background-position:
      0 0,
      0 0;
  }

  to {
    background-position:
      60px 60px,
      60px 60px;
  }
}

@keyframes networkGlowGold {
  0%,
  100% {
    opacity: .58;
    transform: scale(.9);
  }

  50% {
    opacity: 1;
    transform: scale(1.12);
  }
}

@keyframes networkGlowBlue {
  0%,
  100% {
    opacity: .54;
    transform: scale(.9);
  }

  50% {
    opacity: .96;
    transform: scale(1.1);
  }
}

@keyframes networkHeroLine {
  0%,
  18% {
    left: -120px;
    opacity: 0;
  }

  30% {
    opacity: 1;
  }

  58% {
    left: calc(100% + 120px);
    opacity: 0;
  }

  100% {
    left: calc(100% + 120px);
    opacity: 0;
  }
}

@keyframes networkDataPulse {
  0%,
  100% {
    opacity: .15;
    transform: scaleX(.78);
  }

  50% {
    opacity: .8;
    transform: scaleX(1);
  }
}

@keyframes networkContentRise {
  from {
    opacity: 0;
    transform: translateY(16px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes networkBadgeSweep {
  0%,
  22% {
    left: -42px;
    opacity: 0;
  }

  32% {
    opacity: 1;
  }

  52% {
    left: calc(100% + 42px);
    opacity: 0;
  }

  100% {
    left: calc(100% + 42px);
    opacity: 0;
  }
}

@keyframes networkStatusPulse {
  0%,
  100% {
    opacity: .48;
    transform: scale(.82);
  }

  50% {
    opacity: 1;
    transform: scale(1.18);
  }
}

@keyframes networkTextFlow {
  from {
    background-position: 0% 50%;
  }

  to {
    background-position: 200% 50%;
  }
}

@keyframes networkLevelFlow {
  from {
    transform: translateX(-100%);
  }

  to {
    transform: translateX(240%);
  }
}

@keyframes networkNodePulse {
  0%,
  100% {
    box-shadow:
      0 0 0 4px rgba(212, 175, 55, .08),
      0 0 22px rgba(212, 175, 55, .22);
  }

  50% {
    box-shadow:
      0 0 0 6px rgba(212, 175, 55, .12),
      0 0 32px rgba(212, 175, 55, .36);
  }
}

@keyframes networkFormulaSweep {
  0%,
  18% {
    left: -50px;
    opacity: 0;
  }

  30% {
    opacity: 1;
  }

  52% {
    left: calc(100% + 50px);
    opacity: 0;
  }

  100% {
    left: calc(100% + 50px);
    opacity: 0;
  }
}

@keyframes networkAvatarSweep {
  0%,
  22% {
    left: -38px;
    opacity: 0;
  }

  32% {
    opacity: 1;
  }

  52% {
    left: calc(100% + 38px);
    opacity: 0;
  }

  100% {
    left: calc(100% + 38px);
    opacity: 0;
  }
}

@keyframes networkProgressFlow {
  from {
    background-position: -44px 0;
  }

  to {
    background-position: 88px 0;
  }
}

@keyframes networkEmptyOrbit {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* =========================================================
 * 小屏幕
 * ========================================================= */

@media (max-width: 390px) {
  .hero-level-box {
    gap: 8px;
  }

  .hero-level-box div {
    padding-right: 10px;
    padding-left: 10px;
  }

  .hero-level-box span {
    font-size: 10px;
  }

  .hero-level-box strong {
    font-size: 16px;
  }

  .level-road {
    gap: 4px;
  }

  .node-dot {
    width: 37px;
    height: 37px;
    font-size: 11px;
  }

  .level-road::before,
  .level-road::after {
    top: 18px;
  }

  .level-node strong {
    font-size: 11px;
  }

  .level-node span {
    font-size: 8px;
  }
}

/* =========================================================
 * 减少动画
 * ========================================================= */

@media (prefers-reduced-motion: reduce) {
  .network-grid-layer,
  .network-scan-beam,
  .network-aurora,
  .network-particles i,
  .system-state,
  .system-state::after,
  .state-visual,
  .primary-button::before,
  .network-notice::after,
  .hero-premium,
  .hero-premium::before,
  .hero-premium::after,
  .hero-grid-bg,
  .hero-glow-gold,
  .hero-glow-blue,
  .hero-scan-line,
  .hero-data-stream span,
  .hero-content,
  .hero-badge::after,
  .hero-badge i,
  .hero-premium h1 span,
  .level-road::after,
  .level-node .node-dot,
  .section-card::before,
  .stat-card::before,
  .branch-card::before,
  .member-card::before,
  .formula-line::after,
  .avatar::after,
  .member-avatar::after,
  .branch-bar::after,
  .empty-ring {
    animation: none !important;
  }
}


</style>